/**
 * 需要想定 多系列チャートのカード。
 * index.tsx から presentational 部分を切り出した純表示コンポーネント。
 *
 * カード内縦順序 (design L737-803 準拠):
 *   補正モード (DemandForecastWorkspace 側) → 凡例 → 気象観測地点note → weather-strip
 *   → チャート
 * 「気象観測地点note」(design L793-798 `#weather-point-note`) は
 * page.observationPointName (外部データサービス > 気象 > エリア別気象予測地点の設定値、
 * weather データの取得有無と独立) を「凡例の下・WeatherBar の上」に表示する。以前は
 * WeatherBar が自身の先頭行で観測地点名を表示していたが、design の note+strip 構造
 * (2要素) に合わせてこのカード直下へ切り出した (WeatherBar 側の重複表示は削除済み)。
 * hiddenKeys (外部凡例の checkbox 状態) は本コンポーネントが所有し、
 * ForecastChartLegend (表示) と ForecastChart (Line/Area の hide 判定) の
 * 両方に controlled prop として渡す。
 *
 * 過去実績取得失敗 (FETCH_FAILED) 時の系列降格 (core の需要想定値は表示維持) は
 * `buildForecastSectionView` (`models/forecastSectionView.ts`) が行い済みで、
 * 本 component は `view.status.pastActualsFailed` を Alert 表示 gate として読むだけ
 * (D6)。プロバイダ比較系列は spec 準拠調整 (demand-forecast-daily-spec-align,
 * AC-09) で ForecastChart から削除済み、#4072 で proto ごと撤去済みのため、
 * 対応する降格表示も存在しない。
 */
import { useMemo, useState } from 'react'

import type { ForecastWeatherBasis } from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Box,
  Flex,
  Stack,
  TertiaryButton,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import { FaGearRegular } from '@enechain/ecloud-icons'

import { ActionReasonText } from '~/components/common/ActionReasonText'
import { CompactAlert } from '~/components/common/CompactAlert'
import { DsSelect } from '~/components/common/DsSelect'
import { HelpIcon } from '~/components/common/HelpIcon'
import { IconOnlyButton } from '~/components/common/IconOnlyButton'
import { DEMAND_FORECAST_SERIES_COLORS } from '~/constants/chartPalette'
import { iconSize } from '~/constants/iconSize'
import { DeviationBadge } from '~/pages/DemandForecastDaily/components/DeviationBadge'
import { ForecastChart } from '~/pages/DemandForecastDaily/components/ForecastChart'
import { ForecastChartLegend } from '~/pages/DemandForecastDaily/components/ForecastChartLegend'
import { WeatherBar } from '~/pages/DemandForecastDaily/components/WeatherBar'
import { CHART_LEFT_AXIS_WIDTH } from '~/pages/DemandForecastDaily/constants/chartLayout'
import type { WeatherPointOverrideState } from '~/pages/DemandForecastDaily/hooks/useWeatherPointOverride'
import { buildLegendItems } from '~/pages/DemandForecastDaily/models/chartLegend'
import type { ForecastSectionView } from '~/pages/DemandForecastDaily/models/forecastSectionView'
import { targetDayWeatherNotice } from '~/pages/DemandForecastDaily/models/targetDayWeather'
import type { KomaSlots } from '~/pages/DemandForecastDaily/models/types'
import { routePaths } from '~/routes/routePaths'

type Props = {
  /**
   * 断面の描画専用値型 (系列 10 個 + 存在述語 + 日付派生 + 編集 gate)。
   * `buildForecastSectionView` (index.tsx の単一 call site が呼ぶ) の出力を
   * そのまま受け取り、ここでは gate・述語を再計算しない。
   */
  view: ForecastSectionView
  /**
   * 気象観測地点名 (design demand/forecast-today.html L793-798 の
   * `#weather-point-note`)。外部データサービス > 気象 > エリア別気象予測地点で
   * 設定した地点名 (page.observationPointName)。空文字の場合はラベル非表示。
   */
  observationPointName?: string
  /** 選択中エリアの日本語ラベル (observationPointName と併記する「<エリア>エリア」用) */
  areaLabel?: string
  /**
   * 気象観測地点の画面内一時切替 (design demand/forecast-today.html
   * L1128-1200, `.dft-weather-point-row` の select/tooltip/state 行,
   * AC-DFC-008a/008b)。undefined の間は select 等を描画しない (候補未解決・
   * area 未確定時)。
   */
  weatherPointSwitch?: WeatherPointOverrideState
  /**
   * weather-point-in-screen-temp-switch design D6: 確定済み (BG_TOTAL
   * confirmed) / 参照表示 (readonly) 断面では一時切替 UI (地点 select・状態行・
   * 「(一時表示)」prefix) を hide する。**D9 (008c 残差)**: 同じ predicate で
   * 組織マスタ設定への「変更」リンクも hide する (新規 predicate をここで発明
   * しない)。hide predicate の SoT は index.tsx の既存合成 `isReadonly ||
   * (wfCase !== undefined && isDemandForecastReadonly(wfCase))`
   * (= `isCompleted`、komaCard.isCompleted と同じ値)。地点 note 自体 (組織設定
   * 地点名 / 確定済みなら確定時 snapshot 由来) は isCompleted でも表示を維持する
   * (D6/D9 とも一時切替 UI・変更リンクのみが対象、地点 note テキストは対象外)。
   * optional (既定 false): 既存 ForecastCard.test.tsx / DemandForecastWorkspace.test.tsx
   * 等の呼び出し元が本 D6/D9 gating と無関係な観点のテストのため、非対象テストへの
   * scope 外変更を避ける (implementing-feature scope: 差分を足すだけ)。
   */
  isCompleted?: boolean
  /**
   * 次のGC カウントダウン (design `#dft-gc-countdown`)。**気象観測地点 note と
   * 同じ行の右端**に置く (design demand/forecast-today.html L1119-1125)。
   *
   * design はこれを 2026-07-16 に page-header 右端からカード上部へ移設し、
   * 2026-07-17 に気象帯をグラフ直上へ動かした際も同じ行のまま追従させている。
   * 実装は page-header (`headerExtra`) に取り残されていたため #4858 で追随した
   * (デザイナー指摘 2026-08-17「8080 に準拠、グラフの上辺り」)。
   *
   * 表示可否 (当日断面か / 対象日が JST の今日か) の判定は**呼び出し元が持つ**。
   * 本 component は「置き場所」だけを担い、undefined なら行の右端は空になる。
   */
  gcCountdown?: React.ReactNode
  /**
   * 実績トレンドの帯 (乖離率バッジ + 至近Nコマ + 「計画に反映」)。
   * 8080 は凡例の直下に置く。gate の算出は page 側 (index.tsx) が持ち、
   * 本 component は「置き場所」だけを担う (undefined なら帯を出さない)。
   */
  trendBand?: {
    /** 表示 gate (AC-DFD-009 / AC-DFD-013): 対象日=today かつ trendPreview 存在。 */
    hasTrendReflect: boolean
    /** 操作 gate (AC-DFC-007): hasTrendReflect かつ readonly でない。 */
    canApplyTrendReflect: boolean
    /**
     * グループ別の小売がある BG で反映を止めている理由 (AC-8)。指定時は「計画に反映」を
     * 押せなくして理由を文で出す。
     */
    blockedReason?: string
    /**
     * AC-DFD-013: 当日断面で実績が 2 コマそろう前 (avgRatio を出せない) かつ編集できる間。
     * バッジを出さず、「計画に反映」 を非活性にして押せない理由を文で出す。
     */
    isAwaitingActuals: boolean
    avgRatio: number | null
    /** 実績トレンド反映の集計窓 W (コマ数)。 */
    windowKoma: number
    onWindowKomaChange: (koma: number) => void
    onTrendReflectOpen: () => void
  }
  /** 気象バー (design L737-803: 凡例の下・チャートの上) */
  weatherBar: React.ComponentProps<typeof WeatherBar> & {
    /** 対象日の予報の出所 (backend の `forecastBasis`)。 案内の判定にだけ使う。 */
    forecastBasis?: ForecastWeatherBasis
  }
  /** AC-15: 48コマテーブルの列 (時間帯) ホバー時にチャート上の対応ポイントを強調する */
  highlightKoma?: number
  /** AC-DFD-006a-6: 48コマテーブルの参照列見出しにホバー中の系列 (チャートで強調する) */
  emphasizedSeriesKey?: string
  /**
   * チャート外部凡例 (design L785 / L2978-3005) の checkbox 非表示キー集合
   * (spec AC-DFC-009、demand-forecast-danmen-guard、grill Q2)。露点を含む Set
   * 全体を index.tsx から controlled で受け取る (Spinner 経由で本カードの
   * サブツリーが unmount されても、hiddenKeys 自体は unmount しない index.tsx
   * 側に残るため断面往復で保持される)。
   */
  hiddenKeys: Set<string>
  /** hiddenKeys の toggle 結果を呼び出し元へ通知する (index.tsx が state を更新する)。 */
  onHiddenKeysChange: (next: Set<string>) => void
  /** 補正モード (design L742-756): 0=対象コマのみ変更、1=以降へ比例配分 */
  dragMode?: 0 | 1
  /**
   * チャートドラッグ確定 (mouseup) 時のコールバック。mode は確定時の実効補正モード
   * (シフトキー押下中は 1、AC-DFD-069)
   */
  onDragEdit?: (
    koma: number,
    value: number,
    nextSlots: KomaSlots,
    mode: 0 | 1,
  ) => void
  /**
   * AC-WD-005e (Design Note tmp/designing-feature/demand-fix-daily.md
   * Decision #2): 対象日が JST の今日+2 以降 (D+2 以降) か。呼び出し元
   * (index.tsx) が `isTargetDateAtOrAfterDayAfterTomorrowInJst(wfCase.targetDate)`
   * で判定して渡す (ケースの断面 (WorkflowSection) ではなく実カレンダー日付で判定する。
   * `isTargetDateTomorrow` と同じ配線様式)。本 component は判定を持たず、渡された boolean をそのまま表示に使う。
   */
  showThreeHourGranularityLimitNotice?: boolean
  /**
   * AC-WD-005e (翌日断面・05 時前): 翌日断面で VPFD 発表前 (表示中の予報が日別の
   * 源) の案内を出すか。判定は呼び出し元 (index.tsx の
   * `shouldShowBeforeVpfdDailyNotice`) が持ち、本 component は boolean を表示に
   * 使うだけ。D+2 の案内 (`showThreeHourGranularityLimitNotice`) とは別の文言。
   */
  showBeforeVpfdDailyNotice?: boolean
}

/**
 * 気象観測地点の行の **1 段目** が予約する高さ (#9147)。
 *
 * **この段で一番高い要素 = 一時切替 select (`DsSelect`) の外箱の実寸**。
 * 実ブラウザで測ると `.eb-ds-select__control` は **42px**
 * (内側 `__value-container` が 40px + control 自身の上下 border 1px)。
 * design CSS の `--sm = 40px` は**内側の高さ**なので、 flex 行で予約するのは 42px
 * (`components/common/filters/DateStepper.tsx` の `CONTROL_HEIGHT = '40px'` は
 *  内側の高さを使う別の文脈)。
 *
 * 32px にすると、 確定で select が消えた時に
 * 段が 42 → 32px に縮み、 **どの幅でも 10px ぶんチャートが上へ動く**
 * (実測: 520〜1200px の全幅で chartTop が −10px。 折り返しとは別の原因)。
 */
const WEATHER_POINT_NOTE_BAND_MIN_HEIGHT = '42px'

/**
 * 気象観測地点の行の **2 段目** が予約する高さ (#9147)。
 *
 * この段で一番高い要素 = 「◯◯ に戻す」 (`TertiaryButton size="sm"`) の実寸 **32px**
 * (GC カウントダウンは 14px)。 1 段目と値が違うのは、 段ごとに**その段の最も高い要素**へ
 * 合わせているため。 1 段目と同じ 42px にすると、 戻すボタンが無い断面で 10px の
 * 無駄な空きが出る。
 *
 * 測り直し方: `src/harness/index.html` (計測用ハーネス、 未コミット) を vite dev server で開き
 * `?w=<幅>&completed=<0|1>&override=<0|1>` の各断面で `window.__measure()` を比べる。
 */
const WEATHER_POINT_STATE_BAND_MIN_HEIGHT = '32px'

export const ForecastCard = ({
  view,
  observationPointName,
  areaLabel,
  weatherPointSwitch,
  isCompleted = false,
  gcCountdown,
  trendBand,
  weatherBar,
  highlightKoma,
  emphasizedSeriesKey,
  dragMode,
  onDragEdit,
  hiddenKeys,
  onHiddenKeysChange,
  showThreeHourGranularityLimitNotice = false,
  showBeforeVpfdDailyNotice = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  // design `#weather-point-note` は地点未設定なら hidden。 行 (= `.dft-weather-point-row`)
  // 自体は GC が残るので、 note の有無と行の有無を分けて判定する。
  const hasObservationPointNote =
    observationPointName !== undefined && observationPointName !== ''
  // 一時切替 select は候補が解決している間だけ描画する (design `#dft-wx-point`
  // hidden 既定 → 候補確定後に表示)。D6: 確定/参照表示断面 (isCompleted) では
  // 一時切替 UI 自体を hide する (地点 select・状態行・「(一時表示)」prefix の
  // 3 点。地点 note 自体は isCompleted でも表示を続ける)。
  const showWeatherPointSwitch =
    !isCompleted &&
    weatherPointSwitch !== undefined &&
    weatherPointSwitch.candidates.length > 0
  const isWeatherPointOverridden =
    !isCompleted && weatherPointSwitch?.overrideLocationName !== undefined
  /**
   * 一時切替 UI が**この画面で出うるか** (#9147)。
   *
   * `showWeatherPointSwitch` から `!isCompleted` を外したもの = 確定しても変わらない条件。
   * 2 段目 (状態 + GC) を出すかの判定に使う。 `showWeatherPointSwitch` で判定すると
   * 確定で 2 段目ごと消えて段数が変わり、 直そうとしているズレが再発する。
   */
  const hasWeatherPointSwitchCapability =
    weatherPointSwitch !== undefined && weatherPointSwitch.candidates.length > 0
  /**
   * 2 段目を出すか (#9147)。 **確定の前後で変わらない条件だけ**で決める。
   *
   * GC は計画断面が当日 (`sectionSegment === 'today'`) の時だけ渡る (index.tsx) ので、
   * 翌日以降で切替も無い画面には 2 段目を作らない (空の帯を常時出さない)。
   */
  const showWeatherPointStateRow =
    gcCountdown !== undefined || hasWeatherPointSwitchCapability

  /**
   * 地点 note の「（<エリア>エリア）」接尾辞を**独立した Text として**出すための node。
   *
   * select を出す時だけ使う。 select が無い断面では文が 1 つの Text に収まるので、
   * そちらは note の中に直接書いている (テキストノードを割らないほうが、 文全体を
   * 1 要素として読めて a11y・テストの両面で素直なため)。
   */
  const areaSuffixNode =
    hasObservationPointNote && areaLabel !== undefined && areaLabel !== '' ? (
      <Text color="Text.midEmphasis" fontSize="md">
        {t('page:demandForecastDaily.weather.observationPointNoteAreaSuffix', {
          area: areaLabel,
        })}
      </Text>
    ) : null

  /**
   * DS Link を被せて hover 下線・focus ring を design `.ecl-text-link` と揃える。
   * 色は DS 既定 (Text.accentPrimary) が design 値と同じため指定しない。遷移は
   * RouterLink (asChild) に委ねる。
   *
   * D9 (weather-point-in-screen-temp-switch design, AC-DFC-008c 残差): 確定済み /
   * 参照表示 (isCompleted、D6 と同じ既定合成) では組織マスタ設定への恒久変更導線を
   * hide する (地点 note 自体は表示継続)。
   */
  const changeLinkNode =
    hasObservationPointNote && !isCompleted ? (
      // デザイナー指摘 2026-09-16「変更導線も、 8080 同様アイコンボタンにしたい」
      // 「変更導線にホバーしたら、 どの画面遷移するのかを tooltip 出したい」。
      //
      // 8080 `#dft-wxpt-admin-link` は歯車のみの borderless icon-btn で、
      // `aria-label="組織の地点設定を変更"` + 組織共通設定を説明する data-tooltip を
      // 持つ。 テキストの「変更」 2 文字では**何を変えるのか・どこへ飛ぶのか**が
      // どちらも読めなかった。
      //
      // tooltip は 8080 の文面に**遷移先の画面名**を足している (指摘の前段)。
      // 名前は遷移先の実際の呼称 (`common:route.adminWeatherForecastPoints` =
      // 「気象予測地点」) に合わせる。 本画面が値に付けているラベルは
      // 「気象観測地点」 で**呼称が割れている**が、 ここで勝手に寄せると着地した
      // 画面と名前が一致せず、 tooltip の用 (どこへ行くか) を果たさない。
      <IconOnlyButton
        color="Text.midEmphasis"
        asChild
        description={t(
          'page:demandForecastDaily.weather.observationPointChangeLinkTooltip',
        )}
        label={t('page:demandForecastDaily.weather.observationPointChangeLink')}
        size="sm"
        variant="ghost"
      >
        <RouterLink to={routePaths.adminWeatherForecastPoints.path}>
          <FaGearRegular boxSize={iconSize.gridInline} />
        </RouterLink>
      </IconOnlyButton>
    ) : null
  // 左 YAxis 幅を ForecastChart から WeatherBar へ伝播させ、目盛り桁数に応じた
  // 可変幅でも天気帯がチャートと横位置を保つ (fix #5432)。
  const [leftAxisWidth, setLeftAxisWidth] = useState(CHART_LEFT_AXIS_WIDTH)

  // チャート外部凡例 (design L785 / L2978-3005) の checkbox 状態は index.tsx から
  // controlled で受け取る (hiddenKeys/onHiddenKeysChange、demand-forecast-danmen-
  // guard で持ち上げ済み)。
  // 初期表示 (design L2854 `visible = { forecast: true, actual: true, prevDay:
  // true, tempForecast: true }` 準拠): 需要想定値 / 当日実績 / 前日実績 / 予想気温は
  // 初期 ON (= hiddenKeys に含めない)。
  // AC-01 (spec AC-DFC-009 / daily AC-005): 露点 (予測/実績) も初期 ON にする
  // (design L4362-4363 の `_showDewFc` / `_showDewAct` = 既定 true 準拠)。露点は
  // 気温とあわせた需要判断の常用材料のため既定表示とする。
  // AC-04/AC-05: トグル状態はブラウザ再読込までは断面往復越しに保持される (state
  // が index.tsx にあり、route 遷移 (3断面共通 route element・key なし) でも
  // Spinner 経由の subtree unmount でも index.tsx 自体は unmount しないため)。
  // 再読込 (= index.tsx ごと再マウント) でのみ既定 ON に戻る。永続化 (localStorage
  // 等) はしない (Context Pack Constraints)。過去実績・確定履歴はユーザーが追加
  // した時点で ON になる (= 追加時に hiddenKeys へ含めないため、初期値に列挙不要)。
  const toggleKey = (key: string): void => {
    const next = new Set(hiddenKeys)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    onHiddenKeysChange(next)
  }

  // D6: 過去実績取得失敗 (FETCH_FAILED) の降格は builder (ForecastSectionView)
  // 側で行い済み。view.series.pastActuals は既に降格後の値、
  // view.status.pastActualsFailed が Alert 表示 gate。
  const isPastActualsFailed = view.status.pastActualsFailed

  // ForecastChart (右 YAxis 描画) と同一の値 (view.presence) を読む (整列バグ回帰
  // 固定、二重計算をしない。models/forecastSectionView.ts の JSDoc 参照)。
  const showTemperature = view.presence.showTemperature

  /**
   * チャート上でホバー中の「時」 (0〜23)。 気象帯のアイコン強調に使う
   * (2026-09-18 デザイナー指摘「グラフ上にホバーした際、 グラフの上の対象の気象
   * アイコンをわかりやすくしたい」)。
   *
   * チャートと気象帯は別 component なので、 共通の親である本カードが持つ。
   * チャート側は「どの時をホバーしているか」 を通知するだけで強調は描かない。
   */
  const [activeWeatherHour, setActiveWeatherHour] = useState<
    number | undefined
  >(undefined)

  // AC-DFC-013 / 015 / 015a: 気象情報バーとチャートの案内は同じ判定値を読む。
  const weatherNotice = targetDayWeatherNotice({
    forecastBasis: weatherBar.forecastBasis,
    weather: weatherBar.weather,
    actualWeather: weatherBar.actualWeather,
    weatherStatus: weatherBar.weatherStatus,
    temperatureForecastSlots: view.series.temperatureForecast,
    temperatureActualSlots: view.series.temperatureActual,
  })
  // チャートには AC-DFC-013 の案内だけを出す (015 / 015a は気象情報バーだけ)。
  const targetDayWeatherMissing = weatherNotice === 'noWeatherData'

  const legendItems = useMemo(
    () =>
      buildLegendItems({
        t,
        // design L2952 準拠: 凡例の先頭系列ラベルは固定文言「需要想定値」
        // (対象名 = displayLabel ではない)。対象が何かはツールバー / カードタイトル
        // 側で既に表示されているため、凡例からの対象名表示ロスは design 準拠を優先する。
        forecastLabel: t('page:demandForecastDaily.chart.forecast'),
        showEditableBadge: view.editing.isEditable,
        hasTodayActual: view.presence.hasTodayActual,
        hasTrendForecast: view.presence.hasTrendForecast,
        pastActuals: view.series.pastActuals,
        confirmRecordRefs: view.series.confirmRecordRefs,
        showTemperature: view.presence.showTemperature,
        // 気温 (予測/実績) は dot=false の折れ線。単点/離散点では線が描けず凡例
        // だけ残るため、ForecastChart と同一の述語で判定する。予測気温は
        // connectNulls なので有効点 2 点以上、実績気温は connectNulls={false} なので
        // 隣接ペアの有無で判定する (slotHelpers.ts JSDoc 参照)。
        hasTempForecast: view.presence.hasTempForecast,
        hasTempActual: view.presence.hasTempActual,
        // AC-05: 対象日の露点に加え、選択過去日の露点実績も参照する共有述語
        // (ForecastChart の露点 Line 描画可否と同一入力・同一判定、slotHelpers.ts
        // hasDewpointData JSDoc 参照)。
        hasDewpointData: view.presence.hasDewpointData,
        // 凡例 swatch は「線の色の見本」なので、 チャートの描画色と同じ定数を引く
        // (`DEMAND_FORECAST_SERIES_COLORS` が単一 SoT)。 ここで raw hex を書くと
        // 凡例と線が食い違う — 2026-08-31 まで実際にそうなっていた。
        colors: DEMAND_FORECAST_SERIES_COLORS,
      }),
    [t, view],
  )

  // 帯の表示条件。チャート高の再測定 key にも使う (帯はチャートより上にあり、
  // 出入りするとチャートの使える高さが変わるため。ForecastChart の
  // aboveChartRevision / useForecastChartHeight の JSDoc 参照)。
  const visibleTrendBand =
    trendBand !== undefined &&
    trendBand.hasTrendReflect &&
    trendBand.avgRatio !== null
      ? { ...trendBand, avgRatio: trendBand.avgRatio }
      : undefined

  return (
    // 条件を変えた時の影 + スピナーは、天気・グラフ・48 コマ表をまとめた領域 (DemandForecastWorkspace) が
    // 1 枚で出す (領域のローディング。8080 `#dft-content-area` と同じ範囲)。
    <Stack gap={3}>
      {/* 凡例 (design L785): weather-strip の上に独立配置 */}
      <ForecastChartLegend
        hiddenKeys={hiddenKeys}
        items={legendItems}
        onToggle={toggleKey}
      />

      {/* 実績トレンドの帯 (8080 `.dft-trend-bar` = 凡例の直下)。
          ツールバーの「過去実績」グループから移設した
          (2026-08-31 デザイナー指摘「右のグラフを可能な限り 8080 準拠」)。
          グラフを見ながら乖離を確かめる動線なので、グラフと同じカードに置く。

          **gate は移設前と 1 文字も変えていない。** バッジは表示 gate
          (hasTrendReflect = AC-DFD-009 / AC-DFD-013)、ボタンは操作 gate
          (canApplyTrendReflect = AC-DFC-007) で、readonly 中はボタンだけ消えて
          バッジは残る (監視表示は残す)。avgRatio !== null は AC-30
          (有効実績コマ 2 件未満は算出不可)。

          [8080 差異] 8080 は凡例の「実績トレンド予測」トグルを OFF にすると帯ごと
          消すが、写さない。spec (AC-DFD-013) の非表示条件は「実績コマ 2 件未満」
          だけで、凡例トグルは含まれない。トグルは線の表示だけを切り替え、
          帯の数値と操作は残す (2026-09-01 デザイナー確認)。 */}
      {visibleTrendBand !== undefined && (
        <Flex
          alignItems="center"
          justifyContent="center"
          flexWrap="wrap"
          gap={3}
        >
          <DeviationBadge
            avgRatio={visibleTrendBand.avgRatio}
            onWindowKomaChange={visibleTrendBand.onWindowKomaChange}
            windowKoma={visibleTrendBand.windowKoma}
          />
          {visibleTrendBand.canApplyTrendReflect && (
            /* design 実測 (8080): 計画に反映 = `btn btn-tertiary btn-sm` = 32px */
            <TertiaryButton
              onClick={visibleTrendBand.onTrendReflectOpen}
              size="sm"
            >
              {t('page:demandForecastDaily.trendReflect.applyButton')}
            </TertiaryButton>
          )}
          {visibleTrendBand.blockedReason !== undefined && (
            <>
              <ActionReasonText>
                {visibleTrendBand.blockedReason}
              </ActionReasonText>
              <TertiaryButton disabled size="sm">
                {t('page:demandForecastDaily.trendReflect.applyButton')}
              </TertiaryButton>
            </>
          )}
        </Flex>
      )}
      {/* AC-DFD-013: 実績が 2 コマそろうまでは帯を消さずに、 非活性の「計画に反映」 と
          押せない理由を出す (時間がたてば押せるため。 8080 `?demo=few-actuals`)。 */}
      {visibleTrendBand === undefined &&
        trendBand?.isAwaitingActuals === true && (
          <Flex
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap"
            gap={3}
          >
            <ActionReasonText>
              {t('page:demandForecastDaily.trendReflect.awaitingActualsReason')}
            </ActionReasonText>
            <TertiaryButton disabled size="sm">
              {t('page:demandForecastDaily.trendReflect.applyButton')}
            </TertiaryButton>
          </Flex>
        )}

      {/* 気象観測地点note + 次のGC の行 (design `.dft-weather-point-row`
          L1122-1131)。 note は左、 GC は右端 (`margin-left:auto`)。
          **note が非表示でも GC は右端に残す**ため、 行自体の描画条件は
          「note か GC のどちらかがある」 にする (design のコメント:
          「note 非表示時も右端に残すため margin-left:auto」)。
          flexWrap は design 準拠 — グラフ列は幅可変で、 収まらない時は
          squeeze でなく折り返す。
          区切り線は 8080 `.dft-weather-point-row`
          (design/demand/forecast-today.html CSS:135) の
          `border-top: 1px solid var(--border-low); margin-top: var(--space-2);
          padding-top: var(--space-3)` を DS token で写したもの
          (--border-low = Border.lowEmphasis、--space-2/3 = spacing token 2/3)。
          行自体が条件レンダリングなので、 中身が空で線だけ浮く時に線を消す
          8080 CSS:300 相当のフォールバックは不要。 */}
      {(hasObservationPointNote ||
        gcCountdown !== undefined ||
        showWeatherPointSwitch) && (
        // [#6458] 確定 (isCompleted) で一時切替 UI が消えると、この行の高さが
        // select の分だけ縮み、下にあるチャートがその分上へ詰まる (実機計測で
        // チャート上オフセットが 168 → 147 = -21px)。確定操作はレイアウトを
        // 変えない (AC-DFC-007 系にレイアウト変更の規定は無く、#6446 の期待値
        // 「確定の前後でグラフの高さ・レイアウトは変わらない」の延長)。
        //
        // D6 / D9 で「確定・参照表示では一時切替 UI 自体を hide する」と決まって
        // いるので、UI を出し続ける方向では直せない。**行の高さだけを予約する**。
        //
        // [#9147] 1 行に詰めるのをやめ、**2 段固定**にした。
        //
        // #9128 の `minHeight="32px"` は 1 行ぶんしか予約しないので、 行が折り返す幅では
        // 確定で 2 行 → 1 行になって 30〜40px 縮み、 チャートがその分上へ動く。
        // 折り返しの閾値は実測で**行幅 820px**。 実装の定数から viewport 換算すると
        // サイドバーを畳んでも 1380px 以上 (= 820 + 表 480 + カード padding 32 + 48)
        // で折り返すので、 1280 / 1366 / 1440px という一般的な幅すべてで起きる
        // (`TABLE_FALLBACK_WIDTH` / DS `WIDTH_SETTINGS` から算出。 issue #9147 参照)。
        //
        // **段ごとに高さを予約する**ので、 折り返すかどうかに依存しなくなる。
        //   1 段目: 地点の識別 (ラベル / select / エリア接尾辞 / 補足 ? / 変更)
        //   2 段目: 一時切替の状態 (戻す。 無い時は空) + GC カウントダウン (右端)
        //
        // GC を 2 段目へ移したのは、 GC が行幅の約 170px を占めて**折り返しの主因**
        // だから。 2026-07-16 の「page-header からチャート直上へ移して視認性を上げる」
        // 決定は維持される (同じブロックの中・右端寄せのまま)。
        // 8080 も一時切替の状態を `#dft-wxpt-state { flex-basis: 100% }` で独立段に
        // しており、 1 行に畳んでいたのは実装側の判断だった。
        //
        // minHeight は**段側**に置く (要素側に持たせると、 確定で中身が消えたときに
        // 効かない)。 値は各段で一番高い要素の高さ (定数の JSDoc)。
        // paddingTop / borderTop は外側の container が持つので、 段には
        // boxSizing の調整が要らない (minHeight と padding が同じ要素に乗らない)。
        //
        // 幅 0 の spacer 要素は使わない。 各段の Flex は gap={2} を持つので、 幅 0 でも
        // flex item として次の要素との間に 8px の gap を作り、 地点 note が常に右へ
        // ずれる (flexWrap があるため折り返し条件も変わる)。 bot レビュー #9128 の指摘。
        <Flex
          flexDirection="column"
          gap={2}
          marginTop={2}
          paddingTop={3}
          borderColor="Border.lowEmphasis"
          borderTopWidth="1px"
        >
          {/* 1 段目: 地点の識別。 確定でラベル以外すべて消える
              (select / エリア接尾辞 / 補足 ? / 変更) ので、 minHeight はこの段に置く。
              値は **この段で一番高い要素 = 一時切替 select の外箱** に合わせる。
              `WEATHER_POINT_NOTE_BAND_MIN_HEIGHT` の由来はその定数のコメント参照。 */}
          <Flex
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            minHeight={WEATHER_POINT_NOTE_BAND_MIN_HEIGHT}
          >
            {hasObservationPointNote && (
              <>
                {/* デザイナー指摘 (2026-09-16):「項目 label も 8080 準拠」。
                  8080 は 2026-08-17 に `#weather-point-note` を `.form-label`
                  (md / 600 / high-emphasis) へ変えている — プロト側コメント:
                  「項目ラベルなので .form-label を使う。 色・太さは共通 CSS が
                  支配するので inline で上書きしない (旧: mid-emphasis + 400)」。
                  実装は**その 1 つ前の仕様** (md / 400 / mid-emphasis、
                  2026-08-13 時点) を写したまま止まっていたので追随する。

                  Chakra token 経由の fontSize/fontWeight は CSS-in-JS class に
                  解決され jsdom の getComputedStyle では観測できないため、
                  自動テストは置かずコードレビューで見る (GcCountdown.test.tsx と
                  同じ制約)。 */}
                {/* デザイナー指摘 (2026-09-15):「気象観測地点の表示がかぶるのを
                  1 回にしたい」。 select を出す時は**地点名を select だけが持つ**。
                  以前は note の太字と select の選択値で同じ地点名が 2 回出ていた。

                  文は「気象観測地点: 」+ 地点名 +「（<エリア>エリア）」の 1 続きなので、
                  地点名の位置に select を挟む形にする (太字だけ消すと
                  「気象観測地点: （東京エリア）」 という主語の無い文が残る)。
                  DS `Text` は段落要素で、 その中に select の `div` を入れると
                  不正なネストになるため、 **prefix と接尾辞を別々の Text に割って
                  select を兄弟として間に置く**。 行は flex なので見た目は 1 続き。 */}
                <Text textStyle="label" color="Text.highEmphasis">
                  {t(
                    isWeatherPointOverridden
                      ? 'page:demandForecastDaily.weather.observationPointNotePrefixTemporary'
                      : 'page:demandForecastDaily.weather.observationPointNotePrefix',
                  )}
                  {/* select が無い断面 (候補 0 件・確定済み等) では、 地点名の出し手が
                    他に居ないので従来どおり note が太字で出す。 */}
                  {!showWeatherPointSwitch && (
                    <>
                      {/* 8080 はラベルと select が**兄弟**で `gap` が区切るため
                        ラベル自体に区切り文字を持たない。 select が出ない断面
                        (候補なし・確定済み) だけは地点名がこの Text の中に続くので、
                        区切りの空白を明示する (無いと textContent が
                        「気象観測地点東京（東京エリア）」 と連結し、 読み上げでも
                        語が切れない)。 */}{' '}
                      <Box
                        as="span"
                        color="Text.highEmphasis"
                        fontWeight="semibold"
                        marginX={1}
                      >
                        {observationPointName}
                      </Box>
                      {areaLabel !== undefined && areaLabel !== ''
                        ? t(
                            'page:demandForecastDaily.weather.observationPointNoteAreaSuffix',
                            { area: areaLabel },
                          )
                        : null}
                    </>
                  )}
                </Text>
                {/* select が無い断面では文がこの Text 1 つに収まるので、 「変更」 だけを
                  後ろに置く。 select がある時は接尾辞ごと select の右へ回る。 */}
                {!showWeatherPointSwitch && changeLinkNode}
              </>
            )}
            {showWeatherPointSwitch && weatherPointSwitch !== undefined && (
              <>
                {/* 気象観測地点の一時切替 select (design `#dft-wx-point`,
                  L1135-1139)。候補は index.tsx の useWeatherPointOverride が
                  選択中エリアで絞って渡す (ListWeatherForecastPoints 再利用、
                  新規 RPC なし)。表示値は override 中ならその地点、未切替なら
                  組織共通設定 (= 選び直しても no-op の native select 挙動)。
                  地点名の表示はこの select が唯一の出し手 (上の note を参照)。 */}
                <DsSelect
                  // 8080 (`demand/forecast-today.html:1158` の
                  // `style="width:auto;flex-shrink:0"`) と同じ指定。**選択中の地点名を
                  // 省略させない** (2026-09-16 デザイナー指摘「select の現在地は
                  // 省略されないようにしたい」)。
                  //
                  // 旧実装は 140px 固定だった。 flex 行内で幅が動くと隣の状態行や
                  // 右寄せの GC カウントダウンを押し出すため、 幅を確定させる意図
                  // だった。 だがこの 140px は
                  // **地点名 + chevron だけ**を見て決めた値で、 後から入った組織既定の
                  // 接尾辞 (`{{point}} (組織の設定)`) の分を持っておらず、
                  // 「千葉 (…」 と省略されていた。
                  //
                  // 「最長の候補 + 接尾辞」 で固定し直す案は採らない。 候補は実行時に
                  // API から来る気象観測地点なので**最長を静的に決められない**
                  // (足りなければ再び省略され、 多めに取れば短い地点名で間延びする)。
                  // 下限は `minWidth` で従来の 140px を残し、 押し出しの下限を保つ。
                  flexShrink={0}
                  width="auto"
                  minWidth="140px"
                  aria-label={t(
                    'page:demandForecastDaily.weather.temporarySwitchSelectAriaLabel',
                  )}
                  onChange={(locationName) => {
                    weatherPointSwitch.onSelectLocationName(locationName)
                  }}
                  // デザイナー指摘 2026-09-16「select 内の表示も 8080 準拠」。
                  // 8080 `optOf()` は組織の既定地点にだけ ' (組織の設定)' を付ける。
                  // どれが既定かが**開いた瞬間に**読めるので、 戻す前に「元は何か」 を
                  // 確かめられる。
                  //
                  // [8080 未達] 8080 はさらに optgroup 2 群
                  // (「天気・気温・露点が見られる地点」/「気温・露点のみ見られる地点」)
                  // に分けるが、 **写せない**。 分類軸の `_dftWxPointCapable`
                  // (= 天気まで取れるか) に対応するフィールドが proto に無い。
                  // `isAvailable` は別軸で、 proto のコメントどおり
                  // 「JMA マッピングが無い = 今後実装予定」 を表し、 admin の
                  // pulldown でも disabled にしている。 これを group 化に流用すると
                  // **選べない地点が選べてしまう**ので、 disabled のまま据え置く。
                  // group 分けには BFF に天気可否のフィールドが要る (別 issue)。
                  options={weatherPointSwitch.candidates.map((c) => ({
                    value: c.locationName,
                    label:
                      c.locationName ===
                      weatherPointSwitch.orgDefaultLocationName
                        ? t(
                            'page:demandForecastDaily.weather.weatherPointOrgDefaultSuffix',
                            { point: c.locationName },
                          )
                        : c.locationName,
                    disabled: !c.isAvailable,
                  }))}
                  value={
                    weatherPointSwitch.overrideLocationName ??
                    weatherPointSwitch.orgDefaultLocationName
                  }
                />
                {/* select が地点名を持つので、 文の続き (「（<エリア>エリア）」) と
                  「変更」 導線は select の**右**に出す。 これで
                  「気象観測地点: [千葉 ▼]（東京エリア） 変更」 と 1 続きに読める。 */}
                {areaSuffixNode}
                {/* 一時切替中の状態表示と復帰導線 (design `#dft-wxpt-state`)。

                  **[design 差異] 8080 は `flex-basis: 100%` で 2 行目へ折り返すが、
                  実装は select と同じ行に置く** (デザイナー指摘 2026-09-14
                  「『一時表示中』文言を select の右に配置」「組織共通設定の地点に
                  戻す導線を『一時表示中』文言の右に」)。 一時切替は
                  select → 状態 → 復帰 の 1 本の流れなので、 視線を折り返さずに追える
                  ようにする。

                  GC カウントダウン (`marginLeft="auto"`) より **source 順で前**に
                  置くこと。 後ろに置くと auto マージンの右側になり、 行の右端へ
                  飛んで select から離れる。

                  alert の帯にはしない (8080 のコメント: ユーザー自身が選んだ・
                  想定値に影響しない表示状態に warning を当てるのは重さの誤配)。 */}
                {/* デザイナー指摘 2026-09-16「『一時表示中（組織共通設定: 千葉）』
                  文言は消せない？ ラベルの表示と、 表示なくても操作でユーザーに
                  伝わる気がする」。 状態文を落とし、 **戻すボタンの有無**だけで
                  一時切替中を示す。

                  文で言っていたことは既に画面上にある:
                  - 「一時表示中」 → note の接頭辞が「気象観測地点 (一時表示): 」
                  - 切替先の地点名   → すぐ左の select が出している
                  - 戻れること       → このボタン自体 (「組織共通設定の地点に戻す」)

                  唯一この文にしか無かったのは**組織共通設定の地点名**だが、
                  戻すボタンは押せば戻る (取り消しも select で可能) ので、
                  名前を先に読ませる必要はないという判断。

                  枠つき (`TertiaryButton`) はデザイナー指摘 2026-09-15
                  「共通設定に戻すボタンは border ありにしたい」 のまま維持する。
                  状態文が消えた今は、 このボタンが一時切替中の唯一の印なので、
                  枠で「押せる場所」 が読めることがより効いている。 */}
                {/* [#9147] 戻すボタンは **2 段目** に移した (下記)。 1 段目に置くと、
                  一時切替の開始 / 解除でこの段の折り返し段数が変わってチャートが動く。 */}
                {/* 補足 ? (design `#dft-wxpt-help`)。 */}
                <HelpIcon
                  ariaLabel={t(
                    'page:demandForecastDaily.weather.temporarySwitchTooltipAriaLabel',
                  )}
                  content={t(
                    'page:demandForecastDaily.weather.temporarySwitchTooltip',
                  )}
                />
                {/* 歯車 (組織設定への導線) は 1 段目の**末尾**に置く。 8080 は
                  `_placeAdminGear()` で 既定=行末 / 一時切替中=状態行の [戻す] の右 と
                  付け替えて「常に読み順の最後」 を保つが、 **実装は 1 段目の末尾に固定する**。
                  [design 差異 #9147] 2 段固定にしたことで 1 段目 = 地点の識別 +
                  その設定導線 という括りになり、 段をまたいで歯車が動く方が視線の移動が
                  増える。 戻すボタンは 2 段目に常在するので、 付け替えの目的
                  (「設定へ抜ける導線が状態より先に来ない」) は段の順序で満たされる。 */}
                {changeLinkNode}
              </>
            )}
          </Flex>

          {/* 2 段目: 一時切替の状態 + GC カウントダウン。
              **確定の前後で出現条件が変わらない**ように、 `showWeatherPointSwitch`
              (= `!isCompleted` を含む) ではなく `showWeatherPointStateRow` で判定する。
              戻すボタンが無い断面では空の帯になるが、 それがこの段の役目 (高さの予約)。 */}
          {showWeatherPointStateRow && (
            <Flex
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              minHeight={WEATHER_POINT_STATE_BAND_MIN_HEIGHT}
            >
              {isWeatherPointOverridden && weatherPointSwitch !== undefined && (
                // ボタン名は「リセット」 (docs/specs/conventions.md「ボタン名称は挙動で統一する」、
                // #9313)。 戻り先 (組織の設定の地点) はボタン名に含めず tooltip で示す
                // (design `demand/forecast-today.html` の `#dft-wxpt-reset` と同じ)。
                <Tooltip
                  content={t(
                    'page:demandForecastDaily.weather.temporarySwitchResetTooltip',
                    {
                      orgDefault: weatherPointSwitch.orgDefaultLocationName,
                    },
                  )}
                >
                  <TertiaryButton
                    onClick={() => {
                      weatherPointSwitch.onReset()
                    }}
                    size="sm"
                  >
                    {t('page:demandForecastDaily.weather.temporarySwitchReset')}
                  </TertiaryButton>
                </Tooltip>
              )}
              {gcCountdown !== undefined && (
                // design `#dft-gc-countdown` の `margin-left:auto; flex-shrink:0;`。
                // 戻すボタンが無いときも右端に寄せる (= 段の左端に来ない)。
                <Box flexShrink={0} marginLeft="auto">
                  {gcCountdown}
                </Box>
              )}
            </Flex>
          )}
        </Flex>
      )}

      {/* AC-WD-005e: 対象日が翌々日 (D+2) 以降表示中は、3時間ごとの気象粒度が
          翌日までしか無いことを常に案内する (気象データ有無・取得成否に
          関わらず、Design Note tmp/designing-feature/demand-fix-daily.md
          Decision #3)。判定は呼び出し元 (index.tsx) が対象日
          (wfCase.targetDate) 由来で行い、本 component は boolean prop を
          そのまま表示に使う (Decision #2)。D+3〜D+7 は同一 AC-WD-005e の
          残余レンジだが表示画面 (週間/月間) が daily 画面では未実装 — daily
          画面の最遠断面が D+2 のため本 slice の実効トリガーは D+2 のみ
          (D+8 以降の範囲外案内は別 AC-WD-005d、週間/月間の別 slice)。
          placement/style は spec (AC-WD-005e) がデザイナー判断に委譲。
          design `demand/forecast-today.html` の `#dft-granularity-note` は
          weather-strip の**直下**にあるので、 それに合わせて WeatherBar の下に
          置く (デザイナー判断 2026-08-28。 旧コメントは「design HTML に該当
          surface が無い」 としていたが実在した)。
          8080 が note にだけ付けている inline の下余白 24px は写さない。
          写すと Alert→グラフ間だけ 24px になり、 カード内の他の要素間
          (すべて 12px) と揃わなくなるため (意図的な design 差異)。 */}
      {/* 気象バー (design L794-797 の note+weather-strip、G4 降格表示対応)。
          showRightAxis は ForecastChart と同じ値を配り、天気帯を
          チャート X 軸と横位置整列させる。 */}
      <WeatherBar
        {...weatherBar}
        activeHour={activeWeatherHour}
        // AC-WD-008a: 翌々日以降は日別 1 個のアイコン表示にする。 AC-WD-005e の
        // 案内文と同じ boolean (対象日が JST の翌々日以降か) を使う。
        isDailyGranularity={showThreeHourGranularityLimitNotice}
        leftAxisWidth={leftAxisWidth}
        notice={weatherNotice}
        showRightAxis={showTemperature}
      />

      {showThreeHourGranularityLimitNotice && (
        <CompactAlert
          description={t(
            'page:demandForecastDaily.weather.threeHourGranularityLimitNoticeDescription',
          )}
          status="info"
          title={t(
            'page:demandForecastDaily.weather.threeHourGranularityLimitNoticeTitle',
          )}
        />
      )}

      {showBeforeVpfdDailyNotice && (
        <CompactAlert
          description={t(
            'page:demandForecastDaily.weather.beforeVpfdDailyNoticeDescription',
          )}
          status="info"
          title={t(
            'page:demandForecastDaily.weather.beforeVpfdDailyNoticeTitle',
          )}
        />
      )}

      <ForecastChart
        // 帯が出入りする時と、気象情報バーが案内 (AC-DFC-013 / 015 / 015a) との間で
        // 切り替わる時はチャートより上の高さが変わるので、再測定させる。
        aboveChartRevision={`${String(
          visibleTrendBand !== undefined ||
            trendBand?.isAwaitingActuals === true,
        )}:${weatherNotice}`}
        dragMode={dragMode}
        emphasizedSeriesKey={emphasizedSeriesKey}
        hiddenKeys={hiddenKeys}
        highlightKoma={highlightKoma}
        isTargetDayWeatherMissing={targetDayWeatherMissing}
        onDragEdit={onDragEdit}
        onHoverWeatherHourChange={setActiveWeatherHour}
        onLeftAxisWidthChange={setLeftAxisWidth}
        view={view}
      />
      {isPastActualsFailed && (
        <CompactAlert
          description={t(
            'page:demandForecastDaily.chart.pastActualsFetchFailed',
          )}
          status="error"
          title={t('page:demandForecastDaily.chart.pastActualsLabel')}
        />
      )}
    </Stack>
  )
}
